import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, BarChart3, Shield } from "lucide-react";
import { useLang } from "../i18n";
import {
  CONSENT_STORAGE_KEY,
  getStoredConsent,
  setStoredConsent,
} from "../lib/analytics";

const CookieBanner = () => {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const decide = (analytics, status) => {
    setStoredConsent({ status, analytics });
    setVisible(false);
  };

  if (!visible) return null;

  const isIt = lang === "it";

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:left-5 sm:bottom-5 z-50 max-w-md bg-white border-4 border-slate-900 rounded-3xl cartoon-shadow-lg p-5 animate-fade-up"
    >
      <button
        onClick={() => decide(false, "rejected")}
        aria-label="Close"
        className="absolute top-3 right-3 w-8 h-8 bg-yellow-200 border-2 border-slate-900 rounded-full flex items-center justify-center hover:bg-yellow-100"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 bg-yellow-300 border-4 border-slate-900 rounded-full flex items-center justify-center shrink-0 cartoon-shadow-sm">
          <Cookie className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg leading-tight">
            {isIt ? "Biscotti di OBI 🍪" : "OBI's cookies 🍪"}
          </h3>
          <p className="text-sm text-slate-700 mt-1 leading-relaxed">
            {isIt
              ? "Usiamo cookie tecnici essenziali (carrello, lingua, preferiti) e, se acconsenti, cookie di statistica anonimi per migliorare il negozio. "
              : "We use essential technical cookies (cart, language, wishlist) and, with your consent, anonymous analytics cookies to improve the shop. "}
            <Link to="/cookie-policy" className="text-pink-600 underline font-bold">
              Cookie Policy
            </Link>
          </p>

          {showDetails && (
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-start gap-2 bg-amber-50 border-2 border-slate-900 rounded-xl p-2.5">
                <Shield className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold flex items-center justify-between">
                    <span>{isIt ? "Cookie necessari" : "Strictly necessary"}</span>
                    <span className="text-xs bg-emerald-300 border-2 border-slate-900 rounded-full px-2 py-0.5">
                      {isIt ? "Sempre attivi" : "Always on"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600">
                    {isIt
                      ? "Carrello, lingua, preferiti. Senza questi il sito non funziona."
                      : "Cart, language, wishlist. Without these the site won't work."}
                  </p>
                </div>
              </div>
              <label className="flex items-start gap-2 bg-sky-50 border-2 border-slate-900 rounded-xl p-2.5 cursor-pointer">
                <BarChart3 className="w-4 h-4 mt-0.5 text-sky-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold flex items-center justify-between gap-2">
                    <span>{isIt ? "Statistiche (Google Analytics 4)" : "Analytics (Google Analytics 4)"}</span>
                    <input
                      type="checkbox"
                      data-testid="cookie-toggle-analytics"
                      checked={analyticsChecked}
                      onChange={(e) => setAnalyticsChecked(e.target.checked)}
                      className="w-5 h-5 accent-pink-500"
                    />
                  </div>
                  <p className="text-xs text-slate-600">
                    {isIt
                      ? "Misurazioni anonime con IP anonimizzato per capire come migliorare lo shop."
                      : "Anonymous measurement with anonymised IP to understand how to improve the shop."}
                  </p>
                </div>
              </label>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              data-testid="cookie-accept-all"
              onClick={() => decide(true, "accepted")}
              className="cartoon-btn bg-sky-300 hover:bg-sky-200 text-sm py-2 px-3"
            >
              {isIt ? "Accetta tutti" : "Accept all"}
            </button>
            <button
              data-testid="cookie-reject"
              onClick={() => decide(false, "rejected")}
              className="cartoon-btn bg-white hover:bg-yellow-50 text-sm py-2 px-3"
            >
              {isIt ? "Solo necessari" : "Necessary only"}
            </button>
            {!showDetails ? (
              <button
                data-testid="cookie-customize"
                onClick={() => setShowDetails(true)}
                className="text-xs font-bold underline text-slate-700 hover:text-pink-600 ml-1 self-center"
              >
                {isIt ? "Personalizza" : "Customize"}
              </button>
            ) : (
              <button
                data-testid="cookie-save"
                onClick={() => decide(analyticsChecked, analyticsChecked ? "partial" : "rejected")}
                className="cartoon-btn bg-yellow-300 hover:bg-yellow-200 text-sm py-2 px-3"
              >
                {isIt ? "Salva scelte" : "Save choices"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
